import { derived, writable, type Writable } from 'svelte/store';
import type { Readable } from 'svelte/store';

type MediaTypeLike = string;
type MediaItemLike = {
    id: string;
};
type PlaybackTime = number;

export type IndexedMusicKitQueueItem<MediaItem extends MediaItemLike> =
    QueueItem<MediaItem> & { index: number };

// Redefine from https://github.pie.apple.com/media-js/mediakit-js/blob/main/packages/mk-player/src/player/types.ts#L149-L205
// as we don't have access to that synchronously to use as a type and define the derived states.
// Importing it as { PlaybackStates } from '@amp/musickit' will probably pull in more than we want to.
export enum PlaybackStates {
    none = 0,
    loading = 1,
    playing = 2,
    paused = 3,
    stopped = 4,
    ended = 5,
    seeking = 6,
    waiting = 8,
    stalled = 9,
    completed = 10,
}

export interface NowPlayingItemInfo<MediaType extends MediaTypeLike> {
    name: string;
    artistName: string;
    url: string;
    isLibrary: boolean;
    streamingItemId: string;
    mediaItemType: MediaType;
    streamingArtworkUrl?: string;
    id?: string;
}

// Redefine from https://github.pie.apple.com/media-js/mediakit-js/blob/main/packages/musickit/src/controller/features/shuffler.ts#L13-L33
export enum PlayerShuffleMode {
    off,
    songs,
}

// Redefine from https://github.pie.apple.com/media-js/mediakit-js/blob/main/packages/musickit/src/controller/features/repeatable.ts#L6-L34
export enum PlayerRepeatMode {
    none,
    one,
    all,
}

// Redefine from https://github.pie.apple.com/media-js/mediakit-js/blob/main/packages/musickit/src/queue/index.ts#L22-L26
export type QueueItem<MediaItem extends MediaItemLike> = {
    isAutoplay: boolean;
    item: MediaItem;
};

export interface Queue<
    MediaType extends MediaTypeLike,
    MediaItem extends MediaItemLike,
> {
    // currently playing
    nowPlayingItem: QueueItem<MediaItem> | null;
    nowPlayingItemInfo: NowPlayingItemInfo<MediaType> | null;
    nowPlayingItemIndex: number; // index in the full queue, which is a (mixed) combination of userAddedItems and autoplayItems
    state: PlaybackStates | null;

    // queue
    items: IndexedMusicKitQueueItem<MediaItem>[];

    // other fields
    autoplayEnabled: boolean;
    shuffleMode: PlayerShuffleMode;
    repeatMode: PlayerRepeatMode;
}

export interface DerivedPlaybackStates {
    isNone: boolean;
    isPlaying: boolean;
    isLoading: boolean;
    isPaused: boolean;
    isSeeking: boolean;
    isStopped: boolean;
}

type ExternalQueue<MT extends MediaTypeLike, MI extends MediaItemLike> = Queue<
    MT,
    MI
> & {
    nowPlayingItemId: string | null;
    derivedStates: DerivedPlaybackStates;
    historyItems: IndexedMusicKitQueueItem<MI>[];
    unplayedItems: IndexedMusicKitQueueItem<MI>[];
    userAddedItems: IndexedMusicKitQueueItem<MI>[];
    autoplayItems: IndexedMusicKitQueueItem<MI>[];
    unplayedUserItems: IndexedMusicKitQueueItem<MI>[];
    unplayedAutoplayItems: IndexedMusicKitQueueItem<MI>[];
};

export interface PlaybackQueueStore<
    MT extends MediaTypeLike,
    MI extends MediaItemLike,
> {
    external: {
        subscribe: Readable<ExternalQueue<MT, MI>>['subscribe'];
        currentPlaybackTime: Readable<PlaybackTime>;
    };
    internal: {
        // currently playing
        setNowPlayingItem: (queueItem: QueueItem<MI> | null) => void;
        setNowPlayingItemIndex: (index: number | null) => void;
        setNowPlayingItemInfo: (
            nowPlayingItemInfo: NowPlayingItemInfo<MT>,
        ) => void;
        setPlaybackState: (state: PlaybackStates) => void;

        // queue
        setQueueItems: (queueItemsObject: QueueItem<MI>[]) => void;

        setAutoplayEnabled: (value: boolean) => void;
        setCurrentPlaybackTime: (currentPlaybackTime: PlaybackTime) => void;
        setShuffleMode: (shuffleMode: PlayerShuffleMode) => void;
        setRepeatMode: (repeatMode: PlayerRepeatMode) => void;
    };
}

export const createPlaybackQueueStore = <
    MediaType extends MediaTypeLike,
    MediaItem extends MediaItemLike,
>(): PlaybackQueueStore<MediaType, MediaItem> => {
    const initialState: Queue<MediaType, MediaItem> = {
        // currently playing
        nowPlayingItem: null,
        nowPlayingItemIndex: -1,
        nowPlayingItemInfo: null,
        state: null,

        // queue
        items: [],

        autoplayEnabled: false,
        repeatMode: PlayerRepeatMode.none,
        shuffleMode: PlayerShuffleMode.off,
    };

    const internalState: Writable<Queue<MediaType, MediaItem>> =
        writable(initialState);
    // Use a derived store to add dependent/computed fields to the external exposing store
    const externalState: Readable<ExternalQueue<MediaType, MediaItem>> =
        derived(
            internalState,
            (queueState): ExternalQueue<MediaType, MediaItem> => {
                const { items, state, nowPlayingItem, nowPlayingItemIndex } =
                    queueState;

                const historyItems = items.slice(0, nowPlayingItemIndex + 1);
                const unplayedItems = items.slice(nowPlayingItemIndex + 1);

                const userAddedItems = items.filter(
                    ({ isAutoplay }) => !isAutoplay,
                );
                const autoplayItems = items.filter(
                    ({ isAutoplay }) => isAutoplay,
                );

                const unplayedUserItems: IndexedMusicKitQueueItem<MediaItem>[] =
                    [];
                const unplayedAutoplayItems: IndexedMusicKitQueueItem<MediaItem>[] =
                    [];

                unplayedItems.forEach((item) => {
                    // when user adds new items to the end of the queue (playLater) when autoplay is already playing,
                    // move all items until the last user item to the user queue to match desktop.
                    if (
                        item.isAutoplay &&
                        item.index >
                            userAddedItems[userAddedItems.length - 1].index
                    ) {
                        unplayedAutoplayItems.push(item);
                    } else {
                        unplayedUserItems.push(item);
                    }
                });

                return {
                    ...queueState,
                    nowPlayingItemId: nowPlayingItem?.item?.id || null,
                    historyItems,
                    unplayedItems,
                    userAddedItems,
                    autoplayItems,
                    unplayedUserItems,
                    unplayedAutoplayItems,
                    derivedStates: {
                        isNone: state === PlaybackStates.none || !state,
                        isPlaying: state === PlaybackStates.playing,
                        isLoading:
                            state === PlaybackStates.loading ||
                            state === PlaybackStates.waiting,
                        isPaused: state === PlaybackStates.paused,
                        isSeeking: state === PlaybackStates.seeking,
                        isStopped: state === PlaybackStates.stopped,
                    },
                };
            },
        );

    const updateStoreValue = (
        updatedValue: Partial<Queue<MediaType, MediaItem>>,
    ) => {
        internalState.update(
            (
                currentValue: Queue<MediaType, MediaItem>,
            ): Queue<MediaType, MediaItem> => ({
                ...currentValue,
                ...updatedValue,
            }),
        );
    };

    // Extracted the currentPlaybackTime to it's own store to avoid excessive updates to all components
    const playbackTimeStore: Writable<PlaybackTime> = writable(0);

    return {
        internal: {
            setNowPlayingItem: (queueItem) =>
                updateStoreValue({ nowPlayingItem: queueItem }),
            setNowPlayingItemInfo: (nowPlayingItemInfo) =>
                updateStoreValue({ nowPlayingItemInfo }),
            setPlaybackState: (state) => updateStoreValue({ state }),
            setNowPlayingItemIndex: (nowPlayingItemIndex) =>
                updateStoreValue({
                    nowPlayingItemIndex: nowPlayingItemIndex ?? undefined,
                }),
            setQueueItems: (items) =>
                updateStoreValue({
                    items: items.map((item, index) => ({ ...item, index })),
                }),
            setAutoplayEnabled: (autoplayEnabled) => {
                updateStoreValue({ autoplayEnabled });
            },
            setCurrentPlaybackTime: (currentPlaybackTime) => {
                playbackTimeStore.set(currentPlaybackTime);
            },
            setShuffleMode: (shuffleMode) => {
                updateStoreValue({ shuffleMode });
            },
            setRepeatMode: (repeatMode) => {
                updateStoreValue({ repeatMode });
            },
        },
        external: {
            subscribe: externalState.subscribe,
            currentPlaybackTime: { subscribe: playbackTimeStore.subscribe },
        },
    };
};
